import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  Link2,
  SlidersHorizontal,
  Sparkles,
  Star,
  Settings2,
  Upload,
} from "lucide-react";
import { useAppServices } from "../../../hook/services";
import { useMarketplace } from "../../../marketplace/context";
import { Link as LinkType, Campaign, LinkTag } from "../../../types";
import HeroSection from "../../../components/ui/HeroSection";
import EmptyState from "../../../components/ui/EmptyState";
import Button from "../../../components/ui/Button";
import LinkCard from "../../../components/ui/LinkCard";
import Skeleton from "../../../components/ui/Skeleton";
import Badge from "../../../components/ui/Badge";
import Pagination from "../../../components/ui/Pagination";
import ManageTagsModal from "./ManageTagsModal";
import BulkImportModal from "./BulkImportModal";
import { useConfirm } from "../../../components/ui/ConfirmDialog";
import { formatNumber } from "../../../utils";

type SortKey = "newest" | "oldest" | "clicks";

const DAYS = 14;
const PAGE_SIZE = 10;
const dayKey = (d: Date) => d.toISOString().slice(0, 10);

// Zero-fill the server's per-day counts ({ 'YYYY-MM-DD': clicks }) into a fixed
// trailing `days`-day array for the sparkline.
const buildLinkSparkline = (counts: Record<string, number>, days: number) => {
  const out: number[] = [];
  const now = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    out.push(counts[dayKey(d)] || 0);
  }
  return out;
};

const UsageBar = ({
  label,
  current,
  max,
  pct,
  reached,
}: {
  label: string;
  current: number;
  max: number;
  pct: number;
  reached: boolean;
}) => (
  <div>
    <div className="flex items-center justify-between text-[11px] mb-1">
      <span className="text-ink-500 uppercase tracking-wider font-semibold">
        {label}
      </span>
      <span className="text-ink-600 tabular-nums">
        {current}/{max}
      </span>
    </div>
    <div className="w-full h-1.5 bg-ink-100 rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full transition-all ${
          reached
            ? "bg-gradient-to-r from-rose-500 to-amber-500"
            : "bg-brand-gradient"
        }`}
        style={{ width: `${pct}%` }}
      />
    </div>
  </div>
);

const Links = () => {
  const services = useAppServices();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { features, tierLabel, openUpgrade, buildPath } = useMarketplace();
  const [searchParams, setSearchParams] = useSearchParams();
  const goCreate = () => navigate(buildPath("links/new"));
  const goEdit = (link: LinkType) => navigate(buildPath(`links/${link._id}/edit`));
  const [links, setLinks] = useState<LinkType[]>([]);
  const [campaignsById, setCampaignsById] = useState<Record<string, Campaign>>(
    {},
  );
  const [sparklines, setSparklines] = useState<Record<string, number[]>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("newest");
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [favOnly, setFavOnly] = useState(false);
  const [page, setPage] = useState(1);
  // Account-managed tags (the only tags users can assign/filter by).
  const [managedTags, setManagedTags] = useState<LinkTag[]>([]);
  const [addingTag, setAddingTag] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const fetchTags = useCallback(async () => {
    const { response } = await services.link_tags.get();
    if (Array.isArray(response)) setManagedTags(response);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const addTag = async () => {
    const name = newTag.trim().toLowerCase();
    if (!name) {
      setAddingTag(false);
      return;
    }
    const { response } = await services.link_tags.create({ payload: { name } });
    setNewTag("");
    setAddingTag(false);
    await fetchTags();
    if (response?.name) {
      setActiveTag(response.name);
      setFavOnly(false);
    }
  };

  // Legacy ?new=1 entry point now routes to the dedicated create page.
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
      goCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLinks = useCallback(
    async (showLoading = false) => {
      if (showLoading) setLoading(true);
      const promises: any[] = [
        services.url_tracker.get(),
        services.url_tracker_logs.sparklines({ query: `days=${DAYS}` }),
      ];
      if (features.campaigns) promises.push(services.campaigns.get());

      const [linksRes, sparkRes, campaignsRes] = await Promise.all(promises);
      if (Array.isArray(linksRes.response)) setLinks(linksRes.response);

      // Server returns { [trackerId]: { 'YYYY-MM-DD': clicks } } — pre-aggregated
      // and bot-excluded; we just zero-fill each into the fixed day window.
      if (sparkRes.response && typeof sparkRes.response === "object") {
        const byLink = sparkRes.response as Record<string, Record<string, number>>;
        const out: Record<string, number[]> = {};
        for (const id of Object.keys(byLink)) {
          out[id] = buildLinkSparkline(byLink[id], DAYS);
        }
        setSparklines(out);
      }
      if (campaignsRes && Array.isArray(campaignsRes.response)) {
        const map: Record<string, Campaign> = {};
        for (const c of campaignsRes.response as Campaign[]) map[c._id] = c;
        setCampaignsById(map);
      }
      if (showLoading) setLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    fetchLinks(true);
  }, [fetchLinks]);

  const handleDelete = async (link: LinkType) => {
    const ok = await confirm({
      title: `Delete "${link.title || "this link"}"?`,
      message: "This link and its short URL will stop working immediately.",
      confirmLabel: "Delete link",
    });
    if (!ok) return;
    await services.url_tracker.delete({ query: `_id=${link._id}` });
    setLinks((curr) => curr.filter((l) => l._id !== link._id));
  };

  const handleDuplicate = async (link: LinkType) => {
    // Build a clean payload from only the fields Create accepts. (Spreading the
    // whole link object trips the validator on read-only fields like `deleted`,
    // `clicks`, `_id`.) Slug is omitted (must be unique) and clicks reset.
    const payload: Record<string, unknown> = {
      title: `${link.title || "Link"} (copy)`,
      url: link.url,
    };
    const carry = [
      "domain_host",
      "campaign_id",
      "geo_rules",
      "device_rules",
      "rotation",
      "tags",
      "click_tags",
      "append_contact_id",
      "sync_contact",
      "og_title",
      "og_description",
      "og_image",
      "lead_capture",
      "activates_at",
      "expires_at",
      "max_clicks",
    ] as const;
    const src = link as unknown as Record<string, unknown>;
    carry.forEach((k) => {
      const v = src[k];
      if (v !== undefined && v !== null && v !== "") payload[k] = v;
    });
    const { response, error: err } = await services.url_tracker.create({
      payload,
    });
    if (err) {
      await confirm({
        title: "Couldn't duplicate",
        message:
          err.message || "Something went wrong duplicating this link.",
        confirmLabel: "OK",
        cancelLabel: "Close",
        tone: "brand",
      });
      return;
    }
    if (response) setLinks((curr) => [response, ...curr]);
  };

  const handleToggleFavorite = async (link: LinkType) => {
    const favorite = !link.favorite;
    setLinks((curr) => curr.map((l) => (l._id === link._id ? { ...l, favorite } : l)));
    const { error } = await services.url_tracker.update({
      payload: { _id: link._id, favorite },
    });
    if (error)
      setLinks((curr) =>
        curr.map((l) => (l._id === link._id ? { ...l, favorite: !favorite } : l)),
      );
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let result = links.slice();
    if (q) {
      result = result.filter(
        (l) =>
          l.title?.toLowerCase().includes(q) ||
          l.url?.toLowerCase().includes(q),
      );
    }
    if (favOnly) result = result.filter((l) => l.favorite);
    if (activeTag) result = result.filter((l) => l.tags?.includes(activeTag));
    if (sort === "newest") {
      result.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
    } else if (sort === "oldest") {
      result.sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
    } else if (sort === "clicks") {
      result.sort((a, b) => (b.clicks || 0) - (a.clicks || 0));
    }
    // Pinned (favorite) links bubble to the top, preserving sort within each group.
    result.sort((a, b) => (b.favorite ? 1 : 0) - (a.favorite ? 1 : 0));
    return result;
  }, [links, search, sort, activeTag, favOnly]);

  // Reset to the first page whenever the filtered set changes.
  useEffect(() => {
    setPage(1);
  }, [search, sort, activeTag, favOnly]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page],
  );

  const totalClicks = useMemo(
    () => links.reduce((s, l) => s + (l.clicks || 0), 0),
    [links],
  );

  // Active links cap (lifetime)
  const limitReached = links.length >= features.maxLinks;
  const limitPct = Math.min(100, (links.length / features.maxLinks) * 100);

  // Monthly creation cap (rolling 30-day window)
  const createdThisMonth = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return links.filter((l) => new Date(l.createdAt).getTime() >= cutoff)
      .length;
  }, [links]);
  const monthlyReached = createdThisMonth >= features.linksPerMonth;
  const monthlyPct = Math.min(
    100,
    (createdThisMonth / features.linksPerMonth) * 100,
  );

  const createBlocked = limitReached || monthlyReached;
  const blockReason = limitReached
    ? `${tierLabel} plan allows up to ${features.maxLinks} active links`
    : monthlyReached
    ? `${tierLabel} plan allows ${features.linksPerMonth} new links per month`
    : null;

  const Chip = ({ value, label }: { value: SortKey; label: string }) => (
    <button
      onClick={() => setSort(value)}
      className={`h-8 px-3 rounded-lg text-xs font-medium transition-all ${
        sort === value
          ? "bg-ink-900 text-white shadow-sm"
          : "bg-white border border-ink-200 text-ink-600 hover:text-ink-900 hover:border-ink-300"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      <HeroSection
        eyebrow={
          <>
            <Link2 size={11} className="text-brand-500" />
            <span>Links</span>
          </>
        }
        title="Your short links"
        description="Create, share and track. Hover any card for quick actions."
        action={
          createBlocked ? (
            <Button
              variant="gradient"
              size="lg"
              onClick={openUpgrade}
              icon={<Sparkles size={15} strokeWidth={2.5} />}
              title={blockReason || undefined}
            >
              Upgrade to add more
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="lg"
                onClick={() => setBulkOpen(true)}
                icon={<Upload size={15} strokeWidth={2.5} />}
              >
                Bulk import
              </Button>
              <Button
                variant="gradient"
                size="lg"
                onClick={goCreate}
                icon={<Plus size={15} strokeWidth={2.5} />}
              >
                Create link
              </Button>
            </div>
          )
        }
      >
        <div className="flex items-center gap-2 pt-2 flex-wrap">
          <Badge tone="brand">
            <span className="font-display font-bold tabular-nums">
              {formatNumber(links.length)}
              <span className="text-ink-500 font-normal ml-0.5">
                /{formatNumber(features.maxLinks)}
              </span>
            </span>
            <span className="text-ink-500 font-normal ml-1">
              {links.length === 1 ? "link" : "links"}
            </span>
          </Badge>
          <Badge tone="emerald" dot>
            <span className="font-display font-bold tabular-nums">
              {formatNumber(totalClicks)}
            </span>
            <span className="text-ink-500 font-normal ml-1">
              total clicks
            </span>
          </Badge>
        </div>
        <div className="mt-4 max-w-md space-y-3">
          <UsageBar
            label="Active links"
            current={links.length}
            max={features.maxLinks}
            pct={limitPct}
            reached={limitReached}
          />
          <UsageBar
            label="This month"
            current={createdThisMonth}
            max={features.linksPerMonth}
            pct={monthlyPct}
            reached={monthlyReached}
          />
          {createBlocked && blockReason && (
            <button
              onClick={openUpgrade}
              className="inline-flex items-center gap-1.5 text-[11px] text-amber-800 bg-amber-50 ring-1 ring-amber-200/60 rounded-md px-2 py-1 hover:bg-amber-100 transition-colors"
            >
              <Sparkles size={11} />
              {blockReason} — upgrade for more
            </button>
          )}
        </div>
      </HeroSection>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title or URL..."
            className="w-full pl-9 pr-3 h-9 bg-white border border-ink-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-ink-400"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <SlidersHorizontal size={13} className="text-ink-400 mr-1" />
          <Chip value="newest" label="Newest" />
          <Chip value="oldest" label="Oldest" />
          <Chip value="clicks" label="Most clicks" />
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap -mt-1">
        {(() => {
          const tabCls = (active: boolean) =>
            `inline-flex items-center gap-1 h-7 px-2.5 rounded-lg text-xs font-medium transition-all ${
              active
                ? "bg-brand-50 text-brand-700 ring-1 ring-brand-200/60"
                : "text-ink-500 hover:bg-ink-100"
            }`;
          return (
            <>
              <button
                onClick={() => {
                  setActiveTag(null);
                  setFavOnly(false);
                }}
                className={tabCls(!favOnly && !activeTag)}
              >
                All
              </button>
              <button
                onClick={() => {
                  setFavOnly(true);
                  setActiveTag(null);
                }}
                className={tabCls(favOnly)}
              >
                <Star
                  size={11}
                  fill={favOnly ? "currentColor" : "none"}
                  className={favOnly ? "text-amber-500" : ""}
                />
                Favorite
              </button>
              {managedTags.map((t) => (
                <button
                  key={t._id}
                  onClick={() => {
                    setActiveTag((curr) => (curr === t.name ? null : t.name));
                    setFavOnly(false);
                  }}
                  className={tabCls(activeTag === t.name)}
                >
                  {t.color && (
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: t.color }}
                    />
                  )}
                  {t.name}
                </button>
              ))}
              {addingTag ? (
                <input
                  autoFocus
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addTag();
                    else if (e.key === "Escape") {
                      setNewTag("");
                      setAddingTag(false);
                    }
                  }}
                  onBlur={addTag}
                  placeholder="New tag"
                  className="h-7 w-28 px-2 rounded-lg text-xs bg-white border border-ink-200 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                />
              ) : (
                <button
                  onClick={() => setAddingTag(true)}
                  title="Add a tag"
                  className="inline-flex items-center justify-center h-7 w-7 rounded-lg text-ink-500 hover:bg-ink-100 hover:text-ink-900 ring-1 ring-ink-200/70"
                >
                  <Plus size={13} />
                </button>
              )}
              {managedTags.length > 0 && (
                <button
                  onClick={() => setManageTagsOpen(true)}
                  title="Manage tags"
                  className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg text-xs font-medium text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                >
                  <Settings2 size={12} />
                  Manage
                </button>
              )}
            </>
          );
        })()}
      </div>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-[92px] w-full" rounded="rounded-2xl" />
          <Skeleton className="h-[92px] w-full" rounded="rounded-2xl" />
          <Skeleton className="h-[92px] w-full" rounded="rounded-2xl" />
          <Skeleton className="h-[92px] w-full" rounded="rounded-2xl" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-ink-200/70 rounded-2xl shadow-card">
          <EmptyState
            icon={<Link2 size={26} />}
            tone="brand"
            title={search ? "No links match your search" : "No links yet"}
            description={
              search
                ? "Try a different search term or clear the search to see all links."
                : "Your first short link unlocks real-time clicks, geo, and device data."
            }
            action={
              !search && (
                <Button
                  variant="gradient"
                  onClick={goCreate}
                  icon={<Plus size={14} strokeWidth={2.5} />}
                >
                  Create your first link
                </Button>
              )
            }
          />
        </div>
      ) : (
        <div className="space-y-3 anim-in">
          {paged.map((link) => (
            <LinkCard
              key={link._id}
              link={link}
              sparklineData={sparklines[link._id]}
              campaign={
                link.campaign_id ? campaignsById[link.campaign_id] : null
              }
              onEdit={goEdit}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
              onToggleFavorite={handleToggleFavorite}
            />
          ))}
          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={filtered.length}
            onPage={setPage}
            className="pt-1"
          />
        </div>
      )}

      <BulkImportModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onCreated={(created) => setLinks((curr) => [...created, ...curr])}
      />

      <ManageTagsModal
        open={manageTagsOpen}
        tags={managedTags}
        onClose={() => setManageTagsOpen(false)}
        onChanged={async () => {
          // Renames/deletes touch both the tag set and link.tags arrays — and
          // could orphan the active filter, so reset it and reload both.
          setActiveTag(null);
          await Promise.all([fetchTags(), fetchLinks()]);
        }}
      />
    </div>
  );
};

export default Links;
